import { z } from "zod";
import {
  TAKE_RATE_EXCEPTION_FORM_FIELDS,
  TAKE_RATE_EXCEPTION_MAX_APPROVED_BY_LENGTH,
  TAKE_RATE_EXCEPTION_MAX_JUSTIFICATION_LENGTH,
  isTakeRateException,
} from "@/features/private-room/bookings/adapters/take-rate-exception";
import { addDays, format, isAfter, parseISO, startOfMonth } from "date-fns";

export const createSchema = (
  initialStartDate?: string,
  initialEndDate?: string,
  initialTakeRatePercentage?: number,
) =>
  z
    .object({
      packageId: z.string({
        required_error: "Selecione um pacote através de seu nome.",
      }),
      contractResponsibleUserId: z.string().optional(),
      roomResponsibleUserId: z.string().optional(),
      startDate: z
        .string({ required_error: "Insira uma data de início." })
        .refine((date) => !isNaN(Date.parse(date)), {
          message: "Insira uma data válida.",
        })
        .refine(
          (date) => {
            if (!!initialStartDate && initialStartDate === date) {
              return true;
            }

            const minDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
            const startDate = parseISO(date);

            return isAfter(startDate, minDate);
          },
          {
            message:
              "Insira uma data de início posterior a data de início do mês atual.",
          },
        ),
      endDate: z
        .string()
        .refine(
          (date) => {
            if (!date) {
              return true;
            }

            if (!!initialEndDate && initialEndDate === date) {
              return true;
            }

            const minDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
            const endDate = parseISO(date);

            return isAfter(endDate, minDate);
          },
          {
            message:
              "Insira uma data de término posterior a data de início do mês atual.",
          },
        )
        .optional(),
      members: z
        .number({
          required_error: "Insira o número de membros.",
        })
        .min(1, {
          message: "O número de membros deve ser maior que 0.",
        }),
      monthlyCredits: z
        .number({
          required_error: "Insira o número de créditos mensais.",
        })
        .min(1, {
          message: "O número de créditos mensais deve ser maior ou igual 1.",
        })
        .multipleOf(0.01, {
          message:
            "O número de créditos mensais deve ter no máximo 2 casas decimais.",
        }),
      partnerValue: z.string({
        required_error: "Insira o valor do parceiro.",
      }),
      takeRatePercentage: z
        .number({
          required_error: "Insira a taxa de take rate.",
        })
        .min(0, {
          message: "A taxa de take rate não pode ser negativa.",
        }),
      [TAKE_RATE_EXCEPTION_FORM_FIELDS.justification]: z
        .string()
        .max(TAKE_RATE_EXCEPTION_MAX_JUSTIFICATION_LENGTH, {
          message: `A justificativa deve ter no máximo ${TAKE_RATE_EXCEPTION_MAX_JUSTIFICATION_LENGTH} caracteres.`,
        })
        .optional(),
      [TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedBy]: z
        .string()
        .max(TAKE_RATE_EXCEPTION_MAX_APPROVED_BY_LENGTH, {
          message: `O responsável pela aprovação deve ter no máximo ${TAKE_RATE_EXCEPTION_MAX_APPROVED_BY_LENGTH} caracteres.`,
        })
        .optional(),
      [TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedAt]: z.string().optional(),
      dealId: z.string({
        required_error: "Insira o ID do deal do hubspot.",
      }),
      repass: z.number({
        required_error: "Insira o valor do repasse.",
      }),
      noticeInDaysClient: z.number({
        required_error: "Insira o período de aviso prévio do cliente.",
      }),
      noticeInDaysPartner: z.number({
        required_error: "Insira o período de aviso prévio do parceiro.",
      }),
      hasUndeterminedTime: z.boolean().default(false),
      expectedDurationMonths: z.number({
        required_error: "Insira a vigência prevista (em meses).",
      }),
      notes: z.string().optional(),
      renewalIndicator: z.string({
        required_error: "Selecione o índice de reajuste.",
      }),
      acceptanceTerm: z.string({
        required_error: "Insira o termo de aceite.",
      }),
      knowledgeTerm: z.string({
        required_error: "Insira o termo de ciência.",
      }),
      flowType: z.enum(["default", "upsell"]),
      poType: z.enum(["pronto", "personalizado", "sob_medida"]).optional(),
    })
    .refine(
      (data) => {
        if (data.hasUndeterminedTime) {
          return true;
        }
        if (!data.endDate) {
          return false;
        }
        return !isNaN(Date.parse(data.endDate));
      },
      {
        message: "Insira uma data de término válida.",
        path: ["endDate"],
      },
    )
    .refine(
      (data) => {
        if (data.hasUndeterminedTime || !data.endDate) {
          return true;
        }
        const startDate = parseISO(data.startDate);
        const endDate = parseISO(data.endDate);
        const minEndDate = addDays(startDate, data.noticeInDaysClient);
        return isAfter(endDate, minEndDate);
      },
      {
        message:
          "A data de término deve ser após o período de aviso prévio do cliente.",
        path: ["endDate"],
      },
    )
    // As três checagens abaixo só cobram a exceção comercial quando o
    // take rate foi de fato alterado nesta edição: um contrato legado que
    // já está abaixo de 10% continua editável em qualquer outro campo,
    // sem exigir justificativa retroativa (mesma regra que o backend já
    // aplica em TakeRateExceptionSpecificationInput).
    .refine(
      (data) =>
        data.takeRatePercentage === initialTakeRatePercentage ||
        !isTakeRateException(data.takeRatePercentage) ||
        !!(data.takeRateExceptionJustification ?? "").trim(),
      {
        message: "Descreva a justificativa da exceção comercial.",
        path: [TAKE_RATE_EXCEPTION_FORM_FIELDS.justification],
      },
    )
    .refine(
      (data) =>
        data.takeRatePercentage === initialTakeRatePercentage ||
        !isTakeRateException(data.takeRatePercentage) ||
        !!(data.takeRateExceptionApprovedBy ?? "").trim(),
      {
        message: "Informe o responsável pela aprovação da exceção comercial.",
        path: [TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedBy],
      },
    )
    .refine(
      (data) =>
        data.takeRatePercentage === initialTakeRatePercentage ||
        !isTakeRateException(data.takeRatePercentage) ||
        !isNaN(Date.parse(data.takeRateExceptionApprovedAt ?? "")),
      {
        message: "Insira a data da aprovação da exceção comercial.",
        path: [TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedAt],
      },
    );

export const schema = createSchema();
